import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  DEFAULT_TIME_FILTER,
  getTimeRangeFromFilter
} from '@/components/TimeFilter';
import type { CaseAISubmitType } from '@/features/CaseAISubmitDialog/CaseAISubmitDialog.types';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import { casesTimeFilterAtom, internalCasesTimeFilterAtom } from '../Cases';
import { selectedOrganizationAtom } from '../Organizations';
import type { Case, CaseDTO, CaseEscalation, CaseFeedback } from './Case.types';
import { mapCase } from './Case.utils';

export const caseDetailsAtom = atom<Case>();

export const useFetchCaseDetails = (id: string) => {
  const [, setCaseDetails] = useAtom(caseDetailsAtom);
  const handleError = useHandleError();

  const { data, refetch, isSuccess, isLoading, isFetching, isError, error } =
    useQuery<CaseDTO, AxiosError>({
      queryKey: [`cases/${id}`],
      enabled: !!id,
      select: (caseData: CaseDTO) => mapCase(caseData as Case),
      staleTime: 60 * 5000
    });

  useEffect(() => {
    if (isSuccess && data) {
      setCaseDetails(data as Case);
    }
    if (isError) {
      handleError(error, 'Failure to fetch case details.');
    }
  }, [isSuccess, data, isError]);

  return { data, isFetching, isLoading, isSuccess, refetch };
};

export const useSendCaseToAI = () => {
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      caseId,
      submitType,
      prompt
    }: {
      caseId?: string;
      submitType: CaseAISubmitType;
      prompt?: string;
    }) => {
      return axiosClient.put(`/cases/add_to_queue/${caseId}`, {
        submit_type: submitType,
        ...(prompt ? { prompt } : {})
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to submit case.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`cases/${mutation.variables?.caseId}`]
      });
      queryClient.invalidateQueries({ queryKey: ['cases/'] });
      enqueueSnackbar(`Case Submitted Successfully`, {
        variant: 'success'
      });
    }
  });

  return {
    sendCaseToAI: mutation.mutate,
    isLoadingSendCaseToAI: mutation.isPending
  };
};

export const useSubmitFeedback = () => {
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const caseDetails = useAtomValue(caseDetailsAtom);

  const mutation = useMutation({
    mutationFn: (feedback: CaseFeedback) => {
      return axiosClient.put(`/cases/${feedback.case_id}/feedback`, feedback);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to submit feedback.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Feedback Submitted Successfully`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [`cases/${caseDetails?.soc_id}`]
      });
    }
  });

  return {
    submitFeedback: mutation.mutate,
    isLoadingSubmitFeedback: mutation.isPending
  };
};

export const useSubmitEscalation = () => {
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [caseDetails] = useAtom(caseDetailsAtom);
  const [casesTimeFilter] = useAtom(casesTimeFilterAtom);
  const [internalCasesTimeFilter] = useAtom(internalCasesTimeFilterAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { pathname } = useLocation();

  const pageFilters =
    pathname === '/cases'
      ? casesTimeFilter || DEFAULT_TIME_FILTER
      : internalCasesTimeFilter || DEFAULT_TIME_FILTER;

  let { from } = getTimeRangeFromFilter(pageFilters);
  from = dayjs(from).utc().format();
  const date_field =
    pageFilters.split('|')[1] === 'C' ? 'created_at' : 'updated_at';

  const caseTableQueryKey =
    selectedOrganization?.label === 'all'
      ? [`cases/?from_date=${from}&date_field=${date_field}`]
      : [
          `organizations/${selectedOrganization?.label}/cases?from_date=${from}&date_field=${date_field}`
        ];

  const mutation = useMutation({
    mutationFn: (data: CaseEscalation) => {
      return axiosClient.post(`/cases/${data.case_id}/user_escalation`, data);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to escalate case.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`cases/${caseDetails?.soc_id}`]
      });
      queryClient.invalidateQueries({ queryKey: caseTableQueryKey });
      enqueueSnackbar(`Case Escalated Successfully`, {
        variant: 'success'
      });
    }
  });

  return {
    submitEscalation: mutation.mutate,
    isLoadingSubmitEscalation: mutation.isPending,
    isSuccessSubmitEscalation: mutation.isSuccess
  };
};

export default useFetchCaseDetails;
